import { useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, BadgeCheck, AlertTriangle, AlertCircle } from 'lucide-react'
import { AuthContext } from '../../Context/AuthContext'
import { getGeneralDate } from '../../Utils/DateTimeFormat'

export function getExamStatusMeta(exam, { isStudent, userId, preferredRole, customer }) {
  const submissions = isStudent
    ? (exam?.submissions ?? []).filter((s) => s?.submitted_by === userId)
    : exam?.submissions ?? []
  const submissionCount = submissions.length
  const evaluationCount = submissions.filter(
    (s) => (s?.corrected_papers?.length ?? 0) > 0,
  ).length

  if (submissionCount === 0) {
    return { label: 'Submission Pending', tone: 'warn' }
  }

  if (evaluationCount < submissionCount) {
    const pending = submissionCount - evaluationCount
    return {
      label: pending > 1 ? `${pending} Evaluations Pending` : 'Evaluation Pending',
      tone: 'pending',
    }
  }

  if (
    submissionCount < (exam?.sub_sale_orders ?? []).length &&
    preferredRole !== customer &&
    !isStudent
  ) {
    return {
      label: `${(exam.sub_sale_orders ?? []).length - submissionCount} Submission Pending`,
      tone: 'pending',
    }
  }

  return { label: 'Evaluated', tone: 'ok' }
}

const ExamStatusPill = ({ label, tone }) => (
  <span className={`edvi-exam-pill edvi-exam-pill--${tone || 'muted'}`}>
    {tone === 'ok' && <BadgeCheck size={13} strokeWidth={2.25} />}
    {tone === 'pending' && <AlertTriangle size={13} strokeWidth={2.25} />}
    {tone === 'warn' && <AlertCircle size={13} strokeWidth={2.25} />}
    {label}
  </span>
)

const ExamCard = ({ data, batchId, isStudent }) => {
  const navigate = useNavigate()
  const { authState } = useContext(AuthContext)
  const { user_id, preferredRole, customer } = authState
  const status = getExamStatusMeta(data, {
    isStudent,
    userId: user_id,
    preferredRole,
    customer,
  })

  const open = () => navigate(`/dashboard/examDetails/${batchId}/${data.id}`)

  return (
    <article
      className="edvi-exam-card"
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          open()
        }
      }}
      role="link"
      tabIndex={0}
    >
      <div className="edvi-exam-card__head">
        <h3 className="edvi-exam-card__title">
          {data.topics_covered || data.title || 'Untitled test'}
        </h3>
        <ExamStatusPill {...status} />
      </div>

      <div className="edvi-exam-card__meta">
        <div className="edvi-exam-card__meta-item">
          <span className="edvi-exam-card__meta-label">Date of test</span>
          <span className="edvi-exam-card__meta-value">
            {getGeneralDate(data.conducted_on) || '—'}
          </span>
        </div>
        {data.last_date_for_submission && (
          <div className="edvi-exam-card__meta-item">
            <span className="edvi-exam-card__meta-label">Submit by</span>
            <span className="edvi-exam-card__meta-value">
              {getGeneralDate(data.last_date_for_submission)}
            </span>
          </div>
        )}
      </div>

      <div className="edvi-exam-card__foot">
        <span className="edvi-exam-card__meta-label">View details</span>
        <span className="edvi-exam-card__go" aria-hidden>
          <ChevronRight size={16} strokeWidth={2.25} />
        </span>
      </div>
    </article>
  )
}

export { ExamStatusPill }
export default ExamCard
